import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from "@mui/material";
import CloudDownloadOutlinedIcon from "@mui/icons-material/CloudDownloadOutlined";
import { integrationApis } from "../../../api/integrationApis";

const REPORTING_PERIODS = ["Annual", "Quarterly", "Monthly"];

export default function ImportFromSdmModal({ open, onClose, reportId, onImported }) {
  const [projects, setProjects] = useState([]);
  const [loadingProjects, setLoadingProjects] = useState(false);
  const [importing, setImporting] = useState(false);
  const [error, setError] = useState("");
  const [projectId, setProjectId] = useState("");
  const [reportingPeriod, setReportingPeriod] = useState("Annual");
  const [overwrite, setOverwrite] = useState(false);

  useEffect(() => {
    if (!open) return;

    let active = true;
    setError("");
    setLoadingProjects(true);

    integrationApis
      .getSdmProjects()
      .then((data) => {
        if (!active) return;
        const list = data?.projects || [];
        setProjects(list);
        if (list.length > 0) {
          setProjectId((prev) => prev || list[0].id);
        }
      })
      .catch((err) => {
        if (!active) return;
        setError(
          err?.response?.data?.message ||
            err?.message ||
            "Could not load SDM projects. Ensure you are signed in via GE3S SSO and SDM is configured."
        );
      })
      .finally(() => {
        if (active) setLoadingProjects(false);
      });

    return () => {
      active = false;
    };
  }, [open]);

  const handleImport = async () => {
    if (!projectId || !reportId) return;
    setImporting(true);
    setError("");
    try {
      const result = await integrationApis.importFromSdm(reportId, {
        projectId,
        reportingPeriod,
        siteId: "global",
        overwrite,
      });
      onImported?.(result);
      onClose?.();
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          err?.message ||
          "Import failed. Please try again."
      );
    } finally {
      setImporting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <CloudDownloadOutlinedIcon color="primary" />
        Import from SDM
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Pull sustainability data from your Sustainability Data Management (SDM)
          project into this SI report. Mapped fields include water, waste,
          energy, workforce, governance, and more. Scope 1/2 emissions in tCO₂e
          are not computed in SDM and may still need manual entry or GHG tool
          data.
        </Typography>

        {loadingProjects ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress size={32} />
          </Box>
        ) : (
          <>
            <FormControl fullWidth size="small" sx={{ mb: 2 }}>
              <InputLabel id="sdm-project-label">SDM Project</InputLabel>
              <Select
                labelId="sdm-project-label"
                label="SDM Project"
                value={projectId}
                onChange={(e) => setProjectId(e.target.value)}
                disabled={projects.length === 0}
              >
                {projects.map((project) => (
                  <MenuItem key={project.id} value={project.id}>
                    {project.name}
                    {project.sector ? ` — ${project.sector}` : ""}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth size="small" sx={{ mb: 2 }}>
              <InputLabel id="sdm-period-label">Reporting period</InputLabel>
              <Select
                labelId="sdm-period-label"
                label="Reporting period"
                value={reportingPeriod}
                onChange={(e) => setReportingPeriod(e.target.value)}
              >
                {REPORTING_PERIODS.map((period) => (
                  <MenuItem key={period} value={period}>
                    {period}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControlLabel
              control={
                <Checkbox
                  checked={overwrite}
                  onChange={(e) => setOverwrite(e.target.checked)}
                />
              }
              label="Overwrite existing imported KPI values"
            />

            {projects.length === 0 && !error && (
              <Typography variant="body2" color="warning.main" sx={{ mt: 1 }}>
                No SDM projects found for your account.
              </Typography>
            )}
          </>
        )}

        {error && (
          <Typography variant="body2" color="error" sx={{ mt: 2 }}>
            {error}
          </Typography>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={importing}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleImport}
          disabled={importing || !projectId || loadingProjects}
          startIcon={
            importing ? <CircularProgress size={16} color="inherit" /> : null
          }
        >
          {importing ? "Importing…" : "Import data"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
